import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  HatGlasses,
  Loader2,
  Search,
  Trash2,
  X,
} from 'lucide-react';

import {
  CATEGORY_ICONS,
  CATEGORY_STYLES,
  CONFIDENCE_STYLES,
  DEFAULT_CATEGORY_ICON,
  DEFAULT_CATEGORY_STYLE,
  LOCATED_BY_LABELS,
  REDACTION_BLOCKED_MESSAGES,
  SOURCE_LABELS,
} from '../../constants/redaction';
import {
  RedactionRequestError,
  createRedaction,
  fetchRedactionCandidates,
  fetchRedactionCategories,
  resolveRedactionDownloadUrl,
} from '../../lib/redactionApi';
import type {
  CandidateReport,
  ManualRedaction,
  RedactionBox,
  RedactionCandidate,
  RedactionCategory,
  RedactionCategoryInfo,
  RedactionManifest,
  RedactionSelectionPayload,
} from '../../types/redaction';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import RedactionPageView from './RedactionPageView';

const BRAND_BG = 'bg-emerald-800';

interface RedactionModalProps {
  isOpen: boolean;
  recordId: string;
  categoriesHref: string;
  candidatesHref: string;
  createHref: string;
  onClose: () => void;
  onCreated: (manifest: RedactionManifest) => void;
}

type Step = 'categories' | 'review' | 'done';

export default function RedactionModal({
  isOpen,
  recordId,
  categoriesHref,
  candidatesHref,
  createHref,
  onClose,
  onCreated,
}: RedactionModalProps) {
  const [step, setStep] = useState<Step>('categories');
  // What the API can search for, and which of those the reviewer has chosen.
  const [availableCategories, setAvailableCategories] = useState<
    RedactionCategoryInfo[]
  >([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categories, setCategories] = useState<RedactionCategory[]>([]);
  const [termsInput, setTermsInput] = useState('');
  const [report, setReport] = useState<CandidateReport | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [grounds, setGrounds] = useState<Record<string, string>>({});
  const [manualItems, setManualItems] = useState<ManualRedaction[]>([]);
  const [note, setNote] = useState('');
  const [page, setPage] = useState(0);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockedReason, setBlockedReason] = useState<string | null>(null);
  const [manifest, setManifest] = useState<RedactionManifest | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, {
    enabled: isOpen,
    onEscape: () => {
      if (!isWorking) onClose();
    },
  });

  const reset = useCallback(() => {
    setStep('categories');
    setCategories([]);
    setTermsInput('');
    setReport(null);
    setSelectedIds(new Set());
    setGrounds({});
    setManualItems([]);
    setNote('');
    setPage(0);
    setIsWorking(false);
    setError(null);
    setBlockedReason(null);
    setManifest(null);
  }, []);

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  // The list comes from the API so the interface offers exactly what the
  // backend searches for. Everything is selected by default, which fails
  // towards withholding rather than releasing.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setCategoriesLoading(true);
    fetchRedactionCategories(categoriesHref)
      .then((loaded) => {
        if (cancelled) return;
        setAvailableCategories(loaded);
        setCategories(loaded.map((category) => category.value));
      })
      .catch(() => {
        if (cancelled) return;
        setAvailableCategories([]);
        setError('The categories to search for could not be loaded. Close this and try again.');
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, categoriesHref]);

  const groundsByValue = useMemo(() => {
    const entries = report?.withholding_grounds ?? [];
    return new Map(entries.map((ground) => [ground.value, ground]));
  }, [report]);

  const selectedCandidates = useMemo(
    () => (report?.candidates ?? []).filter((c) => selectedIds.has(c.id)),
    [report, selectedIds],
  );

  const visibleBoxes = useMemo(
    () => selectedCandidates.flatMap((candidate) => candidate.boxes),
    [selectedCandidates],
  );

  const drawnBoxes = useMemo(
    () => manualItems.map((item) => item.box),
    [manualItems],
  );

  const needsPublicInterestNote = [
    ...selectedCandidates.map((candidate) => grounds[candidate.id] ?? ''),
    ...manualItems.map((item) => item.ground),
  ].some((value) => {
    const ground = groundsByValue.get(value);
    return ground ? !ground.conclusive : false;
  });

  const missingGrounds = selectedCandidates.filter(
    (candidate) => !grounds[candidate.id],
  );
  const incompleteManual = manualItems.filter(
    (item) => !item.category || !item.ground,
  );

  const canCreate =
    (selectedCandidates.length > 0 || manualItems.length > 0) &&
    missingGrounds.length === 0 &&
    incompleteManual.length === 0 &&
    (!needsPublicInterestNote || note.trim().length > 0);

  const handleFind = async () => {
    setIsWorking(true);
    setError(null);
    setBlockedReason(null);
    try {
      const terms = termsInput
        .split('\n')
        .map((term) => term.trim())
        .filter(Boolean);
      const found = await fetchRedactionCandidates(candidatesHref, {
        categories,
        terms,
      });

      // Everything found is selected by default, with its suggested ground.
      // The reviewer removes what should stay rather than hunting for what
      // should go, which fails towards withholding rather than releasing.
      setReport(found);
      setSelectedIds(new Set(found.candidates.map((candidate) => candidate.id)));
      setGrounds(
        Object.fromEntries(
          found.candidates
            .filter((candidate) => candidate.suggested_ground)
            .map((candidate) => [candidate.id, candidate.suggested_ground as string]),
        ),
      );
      setPage(0);
      setManualItems([]);
      setStep('review');
    } catch (caught) {
      if (caught instanceof RedactionRequestError && caught.reason) {
        setBlockedReason(caught.reason);
      } else {
        setError(
          caught instanceof Error ? caught.message : 'Could not analyse this record',
        );
      }
    } finally {
      setIsWorking(false);
    }
  };

  const handleCreate = async () => {
    setIsWorking(true);
    setError(null);
    try {
      const selections: RedactionSelectionPayload[] = [
        ...selectedCandidates.map((candidate) => ({
          term: candidate.text,
          also_terms: candidate.also_covers ?? [],
          category: candidate.category,
          ground: grounds[candidate.id],
          boxes: candidate.boxes,
          origin: candidate.located_by === 'ocr' ? 'ocr' as const : 'term' as const,
        })),
        ...manualItems.map((item) => ({
          term: null,
          category: item.category as RedactionCategory,
          ground: item.ground,
          boxes: [item.box],
          origin: 'manual' as const,
        })),
      ];
      const created = await createRedaction(createHref, {
        selections,
        public_interest_note: note.trim() || null,
        source_generation: report?.source_generation ?? null,
      });
      setManifest(created);
      setStep('done');
      onCreated(created);
    } catch (caught) {
      if (caught instanceof RedactionRequestError && caught.reason) {
        // The record moved under the review, so the candidates in hand are
        // stale. Send the reviewer back rather than leave them retrying a
        // selection that can no longer be applied.
        setBlockedReason(caught.reason);
        setStep('categories');
        setReport(null);
        setManualItems([]);
      } else {
        setError(
          caught instanceof Error ? caught.message : 'Could not create the copy',
        );
      }
    } finally {
      setIsWorking(false);
    }
  };

  const handleDownload = async () => {
    const href = manifest?._links?.download?.href;
    if (!href) return;
    try {
      const url = await resolveRedactionDownloadUrl(href);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError('Could not generate the download link');
    }
  };

  const toggleCandidate = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDrawBox = (box: RedactionBox) => {
    const id = `drawn-${box.page}-${Math.round(box.x0)}-${Math.round(box.y0)}-${Date.now()}`;
    setManualItems((current) => [
      ...current,
      { id, category: '', ground: '', box },
    ]);
  };

  const updateManualItem = (id: string, patch: Partial<ManualRedaction>) => {
    setManualItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Redact and download"
        tabIndex={-1}
        className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl outline-none"
      >
        <header className="flex items-center justify-between gap-4 border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-emerald-100 p-2.5">
              <HatGlasses className="h-5 w-5 text-emerald-700" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Redact & Download</h2>
              <p className="text-sm text-gray-500">
                Create a copy for release with content permanently removed.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isWorking}
            className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {blockedReason && (
          <BlockedNotice
            reason={blockedReason}
            onDismiss={() => setBlockedReason(null)}
          />
        )}

        {error && (
          <div className="mx-6 mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {step === 'categories' && !blockedReason && (
          <CategoryStep
            available={availableCategories}
            loading={categoriesLoading}
            categories={categories}
            onToggle={(value) =>
              setCategories((current) =>
                current.includes(value)
                  ? current.filter((entry) => entry !== value)
                  : [...current, value],
              )
            }
            termsInput={termsInput}
            onTermsChange={setTermsInput}
          />
        )}

        {step === 'review' && report && (
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-hidden lg:grid-cols-12">
            <div className="flex min-h-0 flex-col overflow-y-auto border-r border-gray-100 lg:col-span-5">
              <CandidateList
                report={report}
                availableCategories={availableCategories}
                selectedIds={selectedIds}
                grounds={grounds}
                manualItems={manualItems}
                onToggle={toggleCandidate}
                onGroundChange={(id, value) =>
                  setGrounds((current) => ({ ...current, [id]: value }))
                }
                onFocusCandidate={(candidate) => {
                  if (candidate.boxes.length > 0) {
                    setPage(candidate.boxes[0].page);
                  }
                }}
                onManualChange={updateManualItem}
                onManualRemove={(id) =>
                  setManualItems((current) => current.filter((item) => item.id !== id))
                }
                onFocusManual={(item) => setPage(item.box.page)}
              />
            </div>
            <div className="flex min-h-0 flex-col p-4 lg:col-span-7">
              <RedactionPageView
                recordId={recordId}
                pages={report.pages}
                pagesWithImages={report.pages_with_images}
                pagesOcrd={report.pages_ocrd}
                boxes={visibleBoxes}
                drawnBoxes={drawnBoxes}
                page={page}
                onPageChange={setPage}
                onDrawBox={handleDrawBox}
              />
            </div>
          </div>
        )}

        {step === 'done' && manifest && (
          <DoneStep manifest={manifest} onDownload={handleDownload} />
        )}

        <footer className="flex items-center justify-between gap-4 border-t border-gray-100 bg-gray-50 px-6 py-4">
          {step === 'review' && report ? (
            <ReviewFooterStatus
              selectedCount={selectedCandidates.length + manualItems.length}
              missingGroundCount={missingGrounds.length + incompleteManual.length}
              needsNote={needsPublicInterestNote}
              note={note}
              onNoteChange={setNote}
            />
          ) : (
            <div className="text-sm text-gray-500">
              {step === 'categories' &&
                'Nothing is changed until you create the copy.'}
            </div>
          )}

          <div className="flex shrink-0 items-center gap-3">
            {step === 'review' && (
              <button
                type="button"
                onClick={() => setStep('categories')}
                disabled={isWorking}
                className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 disabled:opacity-50"
              >
                Back
              </button>
            )}
            {step === 'categories' && (
              <button
                type="button"
                onClick={handleFind}
                disabled={isWorking || categories.length === 0}
                className={`flex items-center rounded-xl px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-200 transition disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_BG}`}
              >
                {isWorking ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Search className="mr-2 h-4 w-4" />
                )}
                {isWorking ? 'Analysing...' : 'Find content'}
              </button>
            )}
            {step === 'review' && (
              <button
                type="button"
                onClick={handleCreate}
                disabled={isWorking || !canCreate}
                className={`flex items-center rounded-xl px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-200 transition disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_BG}`}
              >
                {isWorking ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <HatGlasses className="mr-2 h-4 w-4" />
                )}
                {isWorking ? 'Removing content...' : 'Create redacted copy'}
              </button>
            )}
            {step === 'done' && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-100"
              >
                Done
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

function BlockedNotice({
  reason,
  onDismiss,
}: {
  reason: string;
  onDismiss: () => void;
}) {
  return (
    <div className="mx-6 mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
      <div className="flex-1 text-sm text-amber-900">
        {REDACTION_BLOCKED_MESSAGES[reason] ??
          'This record cannot be redacted automatically.'}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="text-xs font-semibold text-amber-800 underline"
      >
        Dismiss
      </button>
    </div>
  );
}

function CategoryStep({
  available,
  loading,
  categories,
  onToggle,
  termsInput,
  onTermsChange,
}: {
  available: RedactionCategoryInfo[];
  loading: boolean;
  categories: RedactionCategory[];
  onToggle: (value: RedactionCategory) => void;
  termsInput: string;
  onTermsChange: (value: string) => void;
}) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
      <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500">
        What should be considered for withholding?
      </h3>
      {loading && (
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading categories...
        </div>
      )}
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {available.map((category) => {
          const Icon = CATEGORY_ICONS[category.value] ?? DEFAULT_CATEGORY_ICON;
          const checked = categories.includes(category.value);
          return (
            <label
              key={category.value}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                checked
                  ? 'border-emerald-300 bg-emerald-50'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(category.value)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="flex-1">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                  <Icon className="h-4 w-4 text-gray-400" />
                  {category.label}
                </span>
                <span className="mt-0.5 block text-xs text-gray-500">
                  {category.description}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      <h3 className="mt-6 text-sm font-bold uppercase tracking-wider text-gray-500">
        Your own terms
      </h3>
      <p className="mt-1 text-xs text-gray-500">
        One per line. Use this for anything specific to this document that
        automatic detection would not know to look for.
      </p>
      <textarea
        value={termsInput}
        onChange={(event) => onTermsChange(event.target.value)}
        rows={4}
        placeholder={'Neighbour at number 14\nThe blue shed'}
        className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
      />
    </div>
  );
}

function CandidateList({
  report,
  availableCategories,
  selectedIds,
  grounds,
  manualItems,
  onToggle,
  onGroundChange,
  onFocusCandidate,
  onManualChange,
  onManualRemove,
  onFocusManual,
}: {
  report: CandidateReport;
  availableCategories: RedactionCategoryInfo[];
  selectedIds: Set<string>;
  grounds: Record<string, string>;
  manualItems: ManualRedaction[];
  onToggle: (id: string) => void;
  onGroundChange: (id: string, value: string) => void;
  onFocusCandidate: (candidate: RedactionCandidate) => void;
  onManualChange: (id: string, patch: Partial<ManualRedaction>) => void;
  onManualRemove: (id: string) => void;
  onFocusManual: (item: ManualRedaction) => void;
}) {
  const empty = report.candidates.length === 0 && manualItems.length === 0;
  const categoryLabels = new Map(
    availableCategories.map((category) => [category.value, category.label]),
  );

  return (
    <div className="divide-y divide-gray-100">
      {report.text_truncated && (
        <div className="bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
          This document is long and only the first part was analysed
          automatically. Check the later pages yourself.
        </div>
      )}
      {report.ocr_incomplete && (
        <div className="bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
          Not every image page could be read automatically. Draw boxes on
          those pages over anything that should be withheld.
        </div>
      )}

      {manualItems.map((item, index) => (
        <div key={item.id} className="bg-emerald-50/40 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <button
                type="button"
                onClick={() => onFocusManual(item)}
                className="block w-full text-left text-sm font-semibold text-gray-900 hover:text-emerald-700"
              >
                Drawn on page {item.box.page + 1}
                {manualItems.length > 1 ? ` · box ${index + 1}` : ''}
              </button>
              <p className="mt-0.5 text-xs text-gray-500">
                Added by you. Choose a category and a withholding ground.
              </p>
              <select
                value={item.category}
                onChange={(event) =>
                  onManualChange(item.id, {
                    category: event.target.value as RedactionCategory | '',
                    ground:
                      event.target.value &&
                      report.default_grounds[
                        event.target.value as RedactionCategory
                      ]
                        ? report.default_grounds[
                            event.target.value as RedactionCategory
                          ] ?? item.ground
                        : item.ground,
                  })
                }
                className={`mt-2 w-full rounded-lg border px-2 py-1.5 text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${
                  item.category
                    ? 'border-gray-200 text-gray-700'
                    : 'border-amber-300 text-amber-800'
                }`}
                aria-label="Category for drawn box"
              >
                <option value="">Choose a category...</option>
                {availableCategories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
              <select
                value={item.ground}
                onChange={(event) =>
                  onManualChange(item.id, { ground: event.target.value })
                }
                className={`mt-2 w-full rounded-lg border px-2 py-1.5 text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${
                  item.ground
                    ? 'border-gray-200 text-gray-700'
                    : 'border-amber-300 text-amber-800'
                }`}
                aria-label="Withholding ground for drawn box"
              >
                <option value="">Choose a withholding ground...</option>
                {report.withholding_grounds.map((option) => (
                  <option key={option.value} value={option.value}>
                    {`s${option.value} — ${option.label}`}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => onManualRemove(item.id)}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-white hover:text-red-600"
              aria-label="Remove drawn box"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}

      {empty && (
        <div className="px-6 py-8 text-center text-sm text-gray-500">
          Nothing was found in the categories you chose. Draw a box on the
          page over anything that should be withheld, or go back and add your
          own terms.
        </div>
      )}

      {report.candidates.map((candidate) => {
        const checked = selectedIds.has(candidate.id);
        const ground = grounds[candidate.id] ?? '';
        return (
          <div
            key={candidate.id}
            className={`px-4 py-3 transition ${checked ? 'bg-white' : 'bg-gray-50/70'}`}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(candidate.id)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                aria-label={`Withhold ${candidate.text}`}
              />
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => onFocusCandidate(candidate)}
                  className="block w-full truncate text-left text-sm font-semibold text-gray-900 hover:text-emerald-700"
                  title={candidate.text}
                >
                  {candidate.text}
                </button>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                  <span
                    className={`rounded-full px-2 py-0.5 font-semibold ${CATEGORY_STYLES[candidate.category] ?? DEFAULT_CATEGORY_STYLE}`}
                  >
                    {categoryLabels.get(candidate.category) ?? candidate.category}
                  </span>
                  <span className="text-gray-400">
                    {SOURCE_LABELS[candidate.source] ?? candidate.source}
                  </span>
                  {candidate.located_by && candidate.located_by !== 'text_layer' && (
                    <span className="text-gray-400">
                      {LOCATED_BY_LABELS[candidate.located_by]}
                    </span>
                  )}
                  {candidate.confidence && (
                    <span
                      className={CONFIDENCE_STYLES[candidate.confidence] ?? 'text-gray-500'}
                    >
                      {candidate.confidence} confidence
                    </span>
                  )}
                  <span className="text-gray-500 tabular-nums">
                    {candidate.hit_count === 1
                      ? '1 place'
                      : `${candidate.hit_count} places`}
                  </span>
                </div>

                {candidate.hit_count === 0 && checked && (
                  <p className="mt-1.5 rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-800">
                    Not found in the page text or in images. It will still be
                    removed from document properties, form fields and
                    attachments, but draw a box if it appears as a picture.
                  </p>
                )}

                {candidate.reason && (
                  <p className="mt-1 text-xs text-gray-500">{candidate.reason}</p>
                )}

                {candidate.also_covers && candidate.also_covers.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Also withholds:{' '}
                    <span className="font-medium text-gray-700">
                      {candidate.also_covers.join(', ')}
                    </span>
                  </p>
                )}

                {checked && (
                  <select
                    value={ground}
                    onChange={(event) =>
                      onGroundChange(candidate.id, event.target.value)
                    }
                    className={`mt-2 w-full rounded-lg border px-2 py-1.5 text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${
                      ground ? 'border-gray-200 text-gray-700' : 'border-amber-300 text-amber-800'
                    }`}
                    aria-label={`Withholding ground for ${candidate.text}`}
                  >
                    <option value="">Choose a withholding ground...</option>
                    {report.withholding_grounds.map((option) => (
                      <option key={option.value} value={option.value}>
                        {`s${option.value} — ${option.label}`}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReviewFooterStatus({
  selectedCount,
  missingGroundCount,
  needsNote,
  note,
  onNoteChange,
}: {
  selectedCount: number;
  missingGroundCount: number;
  needsNote: boolean;
  note: string;
  onNoteChange: (value: string) => void;
}) {
  return (
    <div className="min-w-0 flex-1">
      <p className="text-sm text-gray-600">
        <span className="font-semibold text-gray-900 tabular-nums">
          {selectedCount}
        </span>{' '}
        {selectedCount === 1 ? 'item' : 'items'} will be removed.
        {missingGroundCount > 0 && (
          <span className="ml-1 font-semibold text-amber-700">
            {missingGroundCount} still{' '}
            {missingGroundCount === 1 ? 'needs' : 'need'} a ground.
          </span>
        )}
      </p>
      {needsNote && (
        <div className="mt-2">
          <label className="block text-xs font-semibold text-gray-600">
            Public interest consideration
            <span className="ml-1 font-normal text-gray-400">
              Required for grounds under section 7(2).
            </span>
          </label>
          <input
            type="text"
            value={note}
            onChange={(event) => onNoteChange(event.target.value)}
            placeholder="Why withholding outweighs the public interest in release"
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        </div>
      )}
    </div>
  );
}

function DoneStep({
  manifest,
  onDownload,
}: {
  manifest: RedactionManifest;
  onDownload: () => void;
}) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-6 py-8">
      <div className="mx-auto max-w-xl text-center">
        <div className="mx-auto w-fit rounded-full bg-emerald-100 p-3">
          <CheckCircle2 className="h-7 w-7 text-emerald-700" />
        </div>
        <h3 className="mt-4 text-lg font-bold text-gray-900">
          Redacted copy created
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {manifest.summary.item_count}{' '}
          {manifest.summary.item_count === 1 ? 'item was' : 'items were'} removed
          from {manifest.summary.box_count}{' '}
          {manifest.summary.box_count === 1 ? 'place' : 'places'}, and the copy
          was checked to confirm none of it survived.
        </p>

        <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm">
          <p className="font-semibold text-gray-900">{manifest.file.name}</p>
          <p className="mt-1 text-xs text-gray-500">
            Withheld under{' '}
            {manifest.summary.grounds.map((ground) => `s${ground}`).join(', ')}.
          </p>
          <p className="mt-1 text-xs text-gray-500">
            The copy keeps the record's{' '}
            {manifest.information_security_classification?.level ?? 'unclassified'}{' '}
            classification, so it is not more widely accessible than the record
            it came from.
          </p>
        </div>

        {manifest._links?.download?.href && (
          <button
            type="button"
            onClick={onDownload}
            className={`mt-5 flex w-full items-center justify-center rounded-xl px-6 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-200 transition ${BRAND_BG}`}
          >
            <Download className="mr-2 h-4 w-4" />
            Download redacted copy
          </button>
        )}
      </div>
    </div>
  );
}
